// Real-estate language guard (plan 2026-10-10 §5.9, RU-500).
//
// A real-estate film is made only in the Studio's film languages; the B2C
// languages Felix offers on WhatsApp (Russian, Ukrainian, Greek, …) never
// reach it. Spanish, Swedish, French, German, Italian, Portuguese and Dutch are
// legitimate Studio film languages, so the guard is this list, not "≠ en".
// Pure helpers only, so they run in node-mode vitest.

import {
  isDefaultLanguage,
  normalizeLanguageCode,
} from "../contact-language/languages";
import type { EchoflicksVideoSession } from "../types";
import { isStudioFilm } from "./studioFilms";

/**
 * English plus the Studio's film languages: the bot's
 * `bot/services/studio/options.py::STUDIO_FILM_LANGUAGES`, which match the
 * `content_languages` of `orchestrator.real_estate.content_language.policy.v1`
 * (echoflicks-infra seeds, both environments).
 */
export const REAL_ESTATE_FILM_LANGUAGES: readonly string[] = [
  "en",
  "es",
  "sv",
  "fr",
  "de",
  "it",
  "pt",
  "nl",
];

export type RealEstateLanguageFacts = Partial<
  Pick<
    EchoflicksVideoSession,
    | "echo_type"
    | "content_language"
    | "studio_agency_id"
    | "studio_echo_id"
    | "whatsapp_user_id"
    | "contact_id"
  >
>;

/** A real-estate Echo: stamped `echo_type`, or a film made in the Studio. */
export function isRealEstateEcho(
  echo: RealEstateLanguageFacts | null | undefined,
): boolean {
  return Boolean(
    echo && (echo.echo_type === "real_estate" || isStudioFilm(echo)),
  );
}

/**
 * The content language of a real-estate Echo that is NOT a real-estate film
 * language ("ru"), else null — also for every non-real-estate Echo and every
 * English or unprojected one.
 */
export function realEstateLanguageViolation(
  echo: RealEstateLanguageFacts | null | undefined,
): string | null {
  if (!isRealEstateEcho(echo) || isDefaultLanguage(echo?.content_language)) {
    return null;
  }
  const code = normalizeLanguageCode(echo?.content_language);
  return code && !REAL_ESTATE_FILM_LANGUAGES.includes(code) ? code : null;
}
